import { onAppDispose } from "../init";
import { persistent } from "./hmr-persistent";

// Cached per container element: a disposed and remounted app (PTC-3919) gets a
// fresh [singleton-container] and must not reuse elements still attached to the
// old, detached one. Keying by container lets the old cache go away with it.
// Persisted across HMR re-execution: otherwise the cache resets and the next
// getSingleton() call recreates each element, orphaning the already-mounted one
// (duplicate dialogs/overlays in the DOM).
const singletonsByContainer = persistent(
    Symbol.for("singleton.elementsByContainer"),
    () => new WeakMap<HTMLElement, Record<string, HTMLElement>>()
);

let containerCache: HTMLElement | undefined;

function getContainer(): HTMLElement {
    if (!containerCache?.isConnected) {
        containerCache = (document.querySelector("[singleton-container]") as HTMLElement | null) ?? undefined;
    }
    if (!containerCache) {
        throw new Error("getSingleton() called without a [singleton-container] in the document");
    }
    return containerCache;
}

// The next getSingleton() must re-resolve against the newly mounted app root.
onAppDispose(() => {
    containerCache = undefined;
});

export function getSingleton(elName: string, lightDom = false) {
    const container = getContainer();

    let singletons = singletonsByContainer.get(container);
    if (!singletons) {
        singletons = {};
        singletonsByContainer.set(container, singletons);
    }

    let el = singletons[elName];

    if (!el) {
        singletons[elName] = el = document.createElement(elName);
        if (lightDom) {
            container.appendChild(el);
        } else {
            container.shadowRoot!.appendChild(el);
        }
    }

    return el;
}

export function singleton(name: string, lightDom = false) {
    return (prototype: object, propertyName: string) => {
        Object.defineProperty(prototype, propertyName, {
            get() {
                return getSingleton(name, lightDom);
            },
            enumerable: true,
            configurable: true,
        });
    };
}
